import { useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, usd, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch, PageHeader, Panel, StatusBadge, Table, Empty, StatCard } from "@/components/common";

const METHODS = [["bank", "Bank Transfer"], ["upi", "UPI"], ["usdt_trc20", "USDT (TRC20)"], ["usdt_bep20", "USDT (BEP20)"]];
const PLACEHOLDER = {
  bank: "Bank name, account holder, account number, IFSC/SWIFT",
  upi: "your-upi@bank",
  usdt_trc20: "Your TRC20 wallet address",
  usdt_bep20: "Your BEP20 wallet address",
};

export default function Withdraw() {
  const { user, reload: reloadUser } = useAuth();
  const { data: s } = useFetch("/public/settings");
  const { data: rows, reload } = useFetch("/withdrawals");
  const [f, setF] = useState({ amount: "", method: "usdt_trc20", destination: "" });
  const [busy, setBusy] = useState(false);
  const fee = s ? (Number(f.amount || 0) * s.withdrawal_fee_percent) / 100 : 0;
  const maxW = user?.max_withdrawal || s?.max_withdrawal;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals", { ...f, amount: Number(f.amount) });
      toast.success("Withdrawal requested. It will be processed after review.");
      setF({ ...f, amount: "", destination: "" });
      reload();
      reloadUser();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Payouts" title="Withdraw funds" subtitle="Withdrawals are reviewed and paid manually by our team." />
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Withdrawable balance" value={usd(user?.balance)} accent testid="withdraw-available" />
        <StatCard label="Locked bonus" value={usd(user?.bonus_balance)} testid="withdraw-locked-bonus" sub="Unlocks after your first approved deposit" />
        <StatCard label="Limits" value={s ? `${usd(s.min_withdrawal)} – ${usd(maxW)}` : "—"} testid="withdraw-limits" sub={s ? `Fee ${s.withdrawal_fee_percent}%` : ""} />
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <Panel title="New withdrawal" className="lg:col-span-5">
          <form onSubmit={submit} className="space-y-4 p-6" data-testid="withdraw-form">
            <div>
              <label className="nx-label">Amount (USD)</label>
              <input type="number" step="0.01" min="0" required className="nx-input num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="withdraw-amount-input" />
            </div>
            <div>
              <label className="nx-label">Method</label>
              <select className="nx-input" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="withdraw-method-select">
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="nx-label">Payout destination</label>
              <textarea rows={3} required minLength={4} className="nx-input" placeholder={PLACEHOLDER[f.method]} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="withdraw-destination-input" />
            </div>
            {Number(f.amount) > 0 && (
              <div className="rounded-lg border border-[#1E2738] bg-[#0B0E14] px-3 py-2 text-xs text-slate-400" data-testid="withdraw-summary">
                Fee <span className="num text-white">{usd(fee)}</span> · You receive <span className="num text-[#10B981]">{usd(Number(f.amount) - fee)}</span>
              </div>
            )}
            {user?.bonus_balance > 0 && (
              <p className="flex items-start gap-2 text-xs text-amber-400/90"><Lock size={13} className="mt-0.5 shrink-0" />Your welcome bonus is not withdrawable until your first deposit is approved.</p>
            )}
            <button className="nx-btn-primary w-full py-3" disabled={busy || s?.withdrawals_enabled === false} data-testid="submit-withdraw-button">
              {busy && <Loader2 size={16} className="animate-spin" />} Request withdrawal
            </button>
          </form>
        </Panel>
        <Panel title="Withdrawal history" className="lg:col-span-7">
          <Table head={["Date", "Method", "Amount", "Net", "Status"]} testid="withdraw-history-table">
            {!rows?.length && <Empty cols={5} text="No withdrawals yet" />}
            {rows?.map((w) => (
              <tr key={w.id}>
                <td className="nx-td">{fmtDate(w.created_at)}</td>
                <td className="nx-td">{w.method_label}</td>
                <td className="nx-td num text-white">{usd(w.amount)}</td>
                <td className="nx-td num">{usd(w.net_amount)}</td>
                <td className="nx-td"><StatusBadge status={w.status} />{w.admin_note && <p className="mt-1 text-[11px] text-slate-500">{w.admin_note}</p>}</td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </div>
  );
}
